/**
 * Signer Link Token — the per-request unguessable link id the auditor said
 * was missing ("broken access control on the link").
 *
 * A signature-request link must be a CAPABILITY URL: possessing the exact
 * link is what grants access, so the link id must be unguessable from the
 * request id alone. {@link issueSignerToken} derives an opaque token from
 * (requestId, secretSalt) with an HMAC-style two-pass keyed SHA-256 —
 * deterministic (the same request always yields the same link, so links
 * can be re-sent) yet unforgeable without the salt.
 *
 * SCOPE, honestly stated: this is NOT authentication and NOT a session.
 * It is a bearer link id — the ESIGN-standard "anyone with this exact link
 * may open this one request" capability, the same trust model DocuSign /
 * DocuSeal signer links use. Server-side rules remain the authority for
 * writes; this gates the public render path.
 *
 * Pure + deterministic: sha256HexSync (crm/docHash) everywhere, no
 * WebCrypto async, no randomness, no clocks.
 */

import { sha256HexSync } from './docHash';

/** Domain-separation context — tokens can never collide with doc hashes. */
const TOKEN_CONTEXT = 'argo:signer-link:v1';

/** Token length in hex chars (160 bits of the outer digest — unguessable). */
const TOKEN_LENGTH = 40;

/**
 * The app-level default salt for signer links. Deployments should override
 * via their own configuration; the default keeps dev/local links working.
 * (The token is a link id, not an auth secret — see module header.)
 */
export const DEFAULT_SIGNER_LINK_SALT = 'argo-signer-link-salt-v1';

/**
 * Mint the opaque signer-link token for one request. HMAC-style
 * construction (outer hash over an inner keyed hash) so length-extension
 * on the plain digest cannot forge sibling tokens. Deterministic: same
 * (requestId, salt) → same token, always.
 */
export function issueSignerToken(requestId: string, secretSalt: string): string {
  if (typeof requestId !== 'string' || requestId.trim() === '') {
    throw new Error('issueSignerToken: requestId must be a non-empty string.');
  }
  if (typeof secretSalt !== 'string' || secretSalt.trim() === '') {
    throw new Error('issueSignerToken: secretSalt must be a non-empty string.');
  }
  const inner = sha256HexSync(`${TOKEN_CONTEXT}|inner|${secretSalt}|${requestId}`);
  const outer = sha256HexSync(`${TOKEN_CONTEXT}|outer|${secretSalt}|${inner}`);
  return outer.slice(0, TOKEN_LENGTH);
}

/**
 * Does this presented token open this request? Recomputes the expected
 * token and compares in constant time (accumulated XOR — no early exit on
 * the first differing char). Malformed inputs are simply false, never a
 * throw: the public page must render an honest "invalid link" state.
 */
export function verifySignerToken(
  token: string,
  requestId: string,
  secretSalt: string,
): boolean {
  if (typeof token !== 'string' || token.length !== TOKEN_LENGTH) return false;
  let expected: string;
  try {
    expected = issueSignerToken(requestId, secretSalt);
  } catch {
    return false;
  }
  let diff = 0;
  for (let i = 0; i < expected.length; i++) {
    diff |= token.charCodeAt(i) ^ expected.charCodeAt(i);
  }
  return diff === 0;
}

/**
 * The shareable signing URL: `<base>?sign=<requestId>&signToken=<token>`.
 * Query params (not a hash route) so the app shell can gate the public
 * sign render BEFORE auth boots. Appends with '&' when the base already
 * carries a query string; trailing slashes/'?'/'#' on the base are trimmed.
 */
export function buildSignLink(
  baseUrl: string,
  requestId: string,
  token: string,
): string {
  const trimmed = baseUrl.replace(/[/?#]+$/, '');
  const sep = trimmed.includes('?') ? '&' : '?';
  return (
    `${trimmed}${sep}sign=${encodeURIComponent(requestId)}` +
    `&signToken=${encodeURIComponent(token)}`
  );
}
